import { Component, OnInit } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { User } from '../Models/User';
import { ProductivityNode } from '../Models/ProductivityNode';
import { ProductivityUserStat } from '../Models/ProductivityUserStat';
import { UserService } from '../services/user.service';

type GroupingPeriod = 'week' | 'month';

interface ProductivityGridRow {
  node: ProductivityNode;
  level: number;
  hasChildren: boolean;
}

interface GridUserColumn {
  userId: number;
  displayName: string;
}

@Component({
  selector: 'app-productivity-report',
  templateUrl: './productivity-report.component.html',
  styleUrls: ['./productivity-report.component.css'],
  standalone: false
})
export class ProductivityReportComponent implements OnInit {
  groupingPeriod: GroupingPeriod = 'month';

  currentYear = new Date().getFullYear();
  availableYears: number[] = [];
  selectedYear: number | 'all' = this.currentYear;
  users: User[] = [];
  selectedUsers: User[] = [];

  // Only a Supervisor may pick which users to report on - a Reporter or Self Verifier only ever
  // sees their own activity, and the Users picker is hidden entirely for them.
  isSupervisor = false;

  loading = false;

  private reportData: ProductivityNode[] = [];

  // Rendered as a grid (users across the top, dates down the side) rather than a tree, so users'
  // stats line up in columns instead of being read as a stacked list of text.
  gridUsers: GridUserColumn[] = [];
  gridRows: ProductivityGridRow[] = [];
  private expandedGridKeys = new Set<string>();
  // Top-level (Year) period keys seen on any previous load, so re-running the report after
  // e.g. adding/removing a user doesn't collapse rows the user already expanded - a key only
  // gets auto-expanded the first time it's ever encountered.
  private seenGridYearKeys = new Set<string>();

  private loggedInUserTag = '';
  private loggedInUserId: number | null = null;

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    const user = this.userService.loggedInUser;
    this.loggedInUserTag = `${user?.lastName}, ${user?.firstName}`;
    this.loggedInUserId = user?.id ?? null;
    this.isSupervisor = user?.roles?.some(r => r.id === 2 || r.code === 'Supervisor') || false;

    for (let y = this.currentYear; y >= this.currentYear - 9; y--) {
      this.availableYears.push(y);
    }

    // Non-supervisors can never pick users, so there's no need to fetch the full user list.
    if (this.isSupervisor) {
      this.loadUsers();
    }
    this.loadReport();
  }

  private loadUsers(): void {
    const params = new HttpParams().set('user', this.loggedInUserTag);
    this.http.get<User[]>('/charitycentral/GetAllUsers', { params }).subscribe({
      next: (users) => {
        this.users = (users || []).slice().sort((a, b) =>
          `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
        );
      },
      error: () => this.toastr.error('Failed to load users.', '', { timeOut: 4000 })
    });
  }

  async loadReport(): Promise<void> {
    this.loading = true;
    try {
      let params = new HttpParams()
        .set('user', this.loggedInUserTag)
        .set('groupingPeriod', this.groupingPeriod);

      if (this.selectedYear !== 'all') params = params.set('year', this.selectedYear);

      if (this.isSupervisor) {
        for (const u of this.selectedUsers) {
          params = params.append('userIds', u.id);
        }
      } else if (this.loggedInUserId != null) {
        // A Reporter/Self Verifier is locked to their own activity regardless of any stale
        // selectedUsers state - there's no UI for them to change it.
        params = params.append('userIds', this.loggedInUserId);
      }

      const result = await lastValueFrom(
        this.http.get<ProductivityNode[]>('/charitycentral/GetProductivityReport', { params })
      );
      this.reportData = result ?? [];

      for (const node of this.reportData) {
        if (!this.seenGridYearKeys.has(node.periodKey)) {
          this.seenGridYearKeys.add(node.periodKey);
          this.expandedGridKeys.add(node.periodKey);
        }
      }
      this.rebuildGridUsers();
      this.rebuildGridRows();
    } catch {
      this.toastr.error('Failed to load productivity report.', '', { timeOut: 4000 });
    } finally {
      this.loading = false;
    }
  }

  // Column set is the union of every user who has a stat anywhere in the tree, since a given
  // month/week/day only lists the users who were active in that specific period.
  private rebuildGridUsers(): void {
    const usersById = new Map<number, string>();

    const visit = (nodes: ProductivityNode[] | null | undefined): void => {
      for (const node of nodes ?? []) {
        for (const stat of node.userBreakdown ?? []) {
          if (!usersById.has(stat.userId)) usersById.set(stat.userId, stat.displayName);
        }
        visit(node.children);
      }
    };
    visit(this.reportData);

    this.gridUsers = Array.from(usersById, ([userId, displayName]) => ({ userId, displayName }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  // Flattens the Year -> Month/Week -> Day tree into a row-per-period list for the grid, only
  // descending into a node's children while that node's key is in expandedGridKeys - this is what
  // lets a week/month "header" row expand in place to reveal its day rows.
  private rebuildGridRows(): void {
    const rows: ProductivityGridRow[] = [];

    const visit = (nodes: ProductivityNode[] | null | undefined, level: number): void => {
      for (const node of nodes ?? []) {
        const hasChildren = !!node.children && node.children.length > 0;
        rows.push({ node, level, hasChildren });
        if (hasChildren && this.expandedGridKeys.has(node.periodKey)) {
          visit(node.children, level + 1);
        }
      }
    };
    visit(this.reportData, 0);

    this.gridRows = rows;
  }

  toggleGridRow(node: ProductivityNode): void {
    if (this.expandedGridKeys.has(node.periodKey)) {
      this.expandedGridKeys.delete(node.periodKey);
    } else {
      this.expandedGridKeys.add(node.periodKey);
    }
    this.rebuildGridRows();
  }

  isGridRowExpanded(node: ProductivityNode): boolean {
    return this.expandedGridKeys.has(node.periodKey);
  }

  getUserStat(node: ProductivityNode, userId: number): ProductivityUserStat | undefined {
    return node.userBreakdown?.find(u => u.userId === userId);
  }

  compareUsers(a: User | null, b: User | null): boolean {
    if (!a || !b) return false;
    return a.id === b.id;
  }
}
